import { FC, useEffect } from 'react'

import {
  FitSelector,
  LoadingSpinner,
  MediaAsContents,
  MobileQuickBuy,
  Size,
  useSupportsHover,
} from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../../../../../../app/_providers/TranslationProvider.client'
import { trackEvent } from '../../../../../../../../../lib/analytics'

import {
  AddingIndicator,
  Body,
  Cta,
  EmbroiderySelector,
  EmbroiderySelectorWrapper,
  FitSelectorWrapper,
  LoadingSpinnerWrapper,
  MobileQuickBuyButton,
  QuickBuyStepsContainer,
  SizeSelector,
  SizeSelectorWrapper,
  StepSlider,
} from './styles'
import { useQuickBuyOverlayController } from './useQuickBuyOverlayController'

import type { IProps } from './IProps'

export const QuickBuyOverlay: FC<IProps> = ({
  embroideryConfig,
  activeProductFinalSale,
  allFitsKeys,
  analyticsContext,
  attributeSelectorContext,
  category,
  coloredVariants,
  ctaOnly = false,
  fitKeys,
  fitNames,
  isCurrentColorCompletelySoldOut,
  isFavorites,
  onAddToCart,
  openQuickBuyModal,
  preferences,
  selectedColorKey,
  selectedFitKey,
  setSelectedFitKey,
  sizes,
  show,
  swPromoEligible,
  useLargeWidthSizeSelectorItems,
  rawCategory,
}) => {
  const {
    quickBuy: strings,
    loadMoreButton: { loading: loadingLabel },
  } = useTranslation()
  const supportsHover = useSupportsHover()

  const {
    height,
    offset,
    resetProgress,
    selectedSize,
    steps,
    step: currentStepKey,
  } = useQuickBuyOverlayController({
    activeProductFinalSale,
    category,
    coloredVariants,
    ctaOnly,
    fitKeys,
    embroideryConfig,
    onAddToCart,
    openQuickBuyModal,
    preferences,
    selectedFitKey,
    setSelectedFitKey,
    sizes,
    supportsHover,
    rawCategory,
  })

  const currentStep = steps[currentStepKey]

  const hasBegunQuickBuyExperience = !!show

  useEffect(() => {
    resetProgress()
  }, [ctaOnly, selectedColorKey, supportsHover, resetProgress])

  return (
    <Body
      hasBegunQuickBuyExperience={hasBegunQuickBuyExperience}
      isFavorites={isFavorites}
      ctaOnly={ctaOnly}
      heightPixels={height}
      hideOnDesktop={isCurrentColorCompletelySoldOut}
      useLargeWidthSizeSelectorItems={useLargeWidthSizeSelectorItems}
    >
      <MediaAsContents lessThan='md'>
        <MobileQuickBuyButton
          aria-label='Quick Buy'
          onClick={steps.initial.onSelect}
          ref={steps.initial.ref}
          {...trackEvent({
            action: 'click',
            category: `${analyticsContext.category} quick buy`,
            label: 'mobile-quick-buy',
          })}
        >
          <MobileQuickBuy />
        </MobileQuickBuyButton>
      </MediaAsContents>
      <MediaAsContents greaterThanOrEqual='md'>
        <StepSlider offsetPixels={offset}>
          {supportsHover === false || ctaOnly ? (
            <Cta
              isUppercase
              onClick={steps.initial.onSelect}
              ref={steps.initial.ref}
              {...trackEvent({
                action: 'click',
                category: `${analyticsContext.category} quick buy`,
                label: 'cta',
              })}
            >
              {embroideryConfig?.quickBuyText ?? strings.overlay.cta}
            </Cta>
          ) : null}
          {supportsHover === true && !ctaOnly ? (
            <QuickBuyStepsContainer isVisible={hasBegunQuickBuyExperience}>
              {steps.fit.enabled ? (
                <FitSelectorWrapper ref={steps.fit.ref}>
                  <FitSelector
                    context={attributeSelectorContext}
                    onFitSelect={steps.fit.onSelect}
                    selected={selectedFitKey}
                    allFits={allFitsKeys}
                    fits={fitKeys}
                    fitNames={fitNames}
                    disableFocus={currentStep !== steps.fit}
                    disableMobileHorizontalSwipe
                    {...trackEvent({
                      action: 'click',
                      category: `${analyticsContext.category} quick buy`,
                      label: 'fit',
                    })}
                  />
                </FitSelectorWrapper>
              ) : null}
              {steps.size.enabled ? (
                <SizeSelectorWrapper
                  extendedFixedWidthItems={useLargeWidthSizeSelectorItems}
                  ref={steps.size.ref}
                >
                  <SizeSelector
                    sizes={sizes}
                    context={attributeSelectorContext}
                    minWidthStyle={useLargeWidthSizeSelectorItems ? 'lg' : 'md'}
                    onSelectSize={(size: Size) => {
                      steps.size.onSelect(size, swPromoEligible)
                    }}
                    selected={selectedSize}
                    disableMobileHorizontalSwipe
                    disableFocus={currentStep !== steps.size}
                    {...trackEvent({
                      action: 'click',
                      category: `${analyticsContext.category} quick buy`,
                      label: 'size',
                    })}
                  />
                </SizeSelectorWrapper>
              ) : null}
              {steps.embroidery.enabled && embroideryConfig && (
                <EmbroiderySelectorWrapper ref={steps.embroidery.ref}>
                  <EmbroiderySelector
                    fixtures={embroideryConfig}
                    image={embroideryConfig.image}
                    mandatoryLogoEmbroidery={embroideryConfig.mandatoryLogoEmbroidery}
                    onSelect={steps.embroidery.onSelect}
                    {...trackEvent({
                      action: 'click',
                      category: `${analyticsContext.category} quick buy`,
                      label: 'embroidery',
                    })}
                  />
                </EmbroiderySelectorWrapper>
              )}
            </QuickBuyStepsContainer>
          ) : null}
          {!isCurrentColorCompletelySoldOut && currentStep === steps.adding ? (
            <AddingIndicator ref={steps.adding.ref}>
              <LoadingSpinnerWrapper>
                <LoadingSpinner label={loadingLabel} size='12px' />
              </LoadingSpinnerWrapper>
              {strings.overlay.addingIndication}
            </AddingIndicator>
          ) : null}
        </StepSlider>
      </MediaAsContents>
    </Body>
  )
}
